import { ChevronLeft, ChevronRight, HeartPulse } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useSidebar } from '../../../contexts/SidebarContext';
import { cn } from '../../../lib/utils';
import { portalNavItems } from './portal-nav-items';

/**
 * Patient-portal sidebar — same ACTO chrome conventions as the clinician rail
 * (indigo/teal active state, collapsible, mobile off-canvas drawer) but its own
 * patient nav. `end` on Home so it isn't marked active for every /portal/* route.
 */
export function PortalSidebar() {
  const { isCollapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const collapsed = isCollapsed && !mobileOpen;

  return (
    <>
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
          data-testid="portal-sidebar-backdrop"
        />
      ) : null}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-full w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-200',
          'md:static md:z-auto md:translate-x-0 md:transition-all',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'md:w-16' : 'md:w-60'
        )}
        data-testid="portal-sidebar"
        data-mobile-open={mobileOpen ? 'true' : 'false'}
      >
        <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-4">
          <HeartPulse className="h-6 w-6 shrink-0 text-primary" aria-hidden />
          {!collapsed ? (
            <span className="text-base font-semibold text-foreground">
              Fluent<span className="font-normal"> MedOps</span>
            </span>
          ) : null}
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {portalNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.to === '/portal'}
                title={collapsed ? item.label : undefined}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground hover:bg-muted hover:text-foreground',
                    collapsed && 'justify-center px-0'
                  )
                }
              >
                <Icon className="h-5 w-5 shrink-0" aria-hidden />
                {!collapsed ? <span>{item.label}</span> : null}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden border-t border-sidebar-border p-2 md:block">
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            {!isCollapsed ? <span>Collapse</span> : null}
          </button>
        </div>
      </aside>
    </>
  );
}
